import { UserCircleIcon, CreditCardIcon, UserGroupIcon, GiftIcon, AdjustmentsIcon, FireIcon, KeyIcon, ShareIcon, GlobeIcon, ColorSwatchIcon } from '@heroicons/react/outline';
import { Switch, Route, Link, useLocation } from 'react-router-dom';
import SettingsProfile from './SettingsProfile';
import SettingsBilling from './SettingsBilling';
import SettingsPermissions from './SettingsPermissions';
import SettingsTeam from './SettingsTeam';
import SettingsReferrals from './SettingsReferrals';
import SettingsFormatting from './SettingsFormatting';
import SettingsAchievements from './SettingsAchievements';
import SettingsSolutionPartner from './SettingsSolutionPartner';
import useUser from '../hooks/useUser';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsLanguage from './SettingsLanguage';
import SettingsBranding from './SettingsBranding';

function SettingsSidebarItem(props: { icon: JSX.Element, label: string, link: string }) {
  const pathname = useLocation().pathname;

  return (
    <Link to={props.link}>
      <div className={`flex items-center p-1 rounded ${pathname === props.link ? "bg-blue-50 text-blue" : "bg-white"}`}>
        {props.icon}
        <div className="ml-4 text-sm font-gilroy font-semibold">{props.label}</div>
      </div>
    </Link>
  );
}

function Settings(props: { contentOffset: number }) {
  const { t } = useTranslation();
  const { user } = useUser();

  useEffect(() => {
    document.title = `${t("Settings")} | Portant`;
    return (() => { document.title = "Portant" })
  }, [t]);

  return (
    <div className="flex border border-gray-200 rounded-lg bg-white p-4 w-full h-full">
      <div className="flex flex-col gap-4 pt-4 pr-4 w-52 h-full border-r border-gray-200 flex-shrink-0">
        <SettingsSidebarItem icon={<UserCircleIcon className="w-6 h-6" />} label={t("Profile")} link="/settings" />
        <SettingsSidebarItem icon={<UserGroupIcon className="w-6 h-6" />} label={t("Team")} link="/settings/team" />
        <SettingsSidebarItem icon={<CreditCardIcon className="w-6 h-6" />} label={t("Billing")} link="/settings/billing" />
        <SettingsSidebarItem icon={<ShareIcon className="w-6 h-6" />} label={t("Integrations")} link="/settings/permissions" />
        <SettingsSidebarItem icon={<GiftIcon className="w-6 h-6" />} label={`${t("Referrals")} ${!user?.isPartnerAccount ? t("& Credits") : ''}`} link="/settings/referrals" />
        <SettingsSidebarItem icon={<ColorSwatchIcon className="w-6 h-6" />} label={t("Branding")} link="/settings/branding" />
        <SettingsSidebarItem icon={<AdjustmentsIcon className="w-6 h-6" />} label={t("Formatting")} link="/settings/formatting" />
        {/* TODO: Enable link to language page when translations are ready */}
        <SettingsSidebarItem icon={<GlobeIcon className="w-6 h-6" />} label={t("Language")} link="/settings/language" />
        <SettingsSidebarItem icon={<FireIcon className="w-6 h-6" />} label={t("Achievements")} link="/settings/achievements" />
        {(user?.isPartnerAccount || (user?.teams.length ?? 0) > 1) &&
          <SettingsSidebarItem icon={<KeyIcon className="w-6 h-6" />} label={t("Solution Partner")} link="/settings/partner" />
        }
      </div>
      <Switch>
        <Route path="/settings/billing/success" component={SettingsBilling} />
        <Route path="/settings/billing" component={SettingsBilling} />
        <Route path="/settings/permissions" component={SettingsPermissions} />
        <Route path="/settings/team" component={SettingsTeam} />
        <Route path="/settings/referrals" component={SettingsReferrals} />
        <Route path="/settings/branding" component={SettingsBranding} />
        <Route path="/settings/formatting" component={SettingsFormatting} />
        <Route path="/settings/language" component={SettingsLanguage} />
        <Route path="/settings/achievements" component={SettingsAchievements} />
        {(user?.isPartnerAccount || (user?.teams.length ?? 0) > 1) &&
          <Route path="/settings/partner" component={SettingsSolutionPartner} />
        }
        <Route path="/settings" component={SettingsProfile} />
      </Switch>
    </div>
  );
}

export default Settings;
